import React, { useMemo } from 'react';

import { useQuery } from '@apollo/client/react';
import { Col } from '@opendoor/atoms/box';
import { Form } from '@opendoor/atoms/form';
import { Navigate } from 'react-router';

import { ERROR_PAGE_ROUTE } from '../../src/routes/routes';
import { GetMarketOptionsDocument } from '../graphql/queries/__generated__/GetMarketOptions';
import { Loader } from './Loader';

type MarketOptionsProps = {
  /** Partner whose branches back the market list; empty until one is chosen. */
  partnerIdentifier: string;
  /** Label for the select. */
  title: string;
  /** Placeholder shown before a market is chosen. */
  placeholder: string;
  /** Whether the select is inert (no partner chosen yet). */
  isDisabled: boolean;
  /** Whether the free-text "Other" field is required and rendered. */
  showOtherField: boolean;
  /** Suppresses this section while the partner list is still loading. */
  partnerOptionsLoading: boolean;
};

const MarketOptions: React.FC<MarketOptionsProps> = (props: MarketOptionsProps) => {
  const hasPartnerSelected = Boolean(props.partnerIdentifier);
  const { loading, error, data } = useQuery(GetMarketOptionsDocument, {
    variables: { identifier: props.partnerIdentifier },
    skip: !hasPartnerSelected,
  });

  const marketOptions = useMemo(() => {
    return (
      data?.signupPartnerBranches
        .filter((branch) => branch.marketActive)
        .map((branch) => ({
          value: branch.regionIdentifier,
          label: branch.marketDisplayName,
        }))
        .sort((a, b) => (a.label > b.label ? 1 : -1)) || []
    );
  }, [data]);

  if (error) {
    return <Navigate to={ERROR_PAGE_ROUTE} replace />;
  }

  return props.partnerOptionsLoading ? null : (
    <Col className="pb-14">
      {loading ? (
        <Loader size="large" />
      ) : (
        <>
          <Form.Combobox
            name="market"
            label={props.title}
            placeholder={props.placeholder}
            options={marketOptions}
            disabled={props.isDisabled}
            analyticsName="market-options-dropdown"
          />
          {props.showOtherField && (
            <Col className="pt-4">
              <Form.Input
                name="otherMarket"
                label="Other"
                placeholder="Please specify"
                className="w-full"
                analyticsName="partnership-dashboard-fe-other-market-input"
              />
            </Col>
          )}
        </>
      )}
    </Col>
  );
};

export default MarketOptions;
